// src/pages/auth/RegisterPage.jsx - CLEAN MODERN VERSION
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import InputField from '../../components/common/InputField';
import Button from '../../components/common/Button';
import { registerUser } from '../../services/authService';
import { 
  FaUser, FaEnvelope, FaPhone, FaLock, FaEye, FaEyeSlash,
  FaPlane, FaCheckCircle, FaTimesCircle, FaGlobe, FaUsers, FaHeart
} from 'react-icons/fa';
import { MdOutlineMarkEmailRead } from 'react-icons/md';

const RegisterPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    hasSpecial: false,
    minLength: false
  });
  
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, touchedFields },
  } = useForm({
    mode: 'onChange'
  });
  
  const password = watch('password', '');
  const email = watch('email', '');
  const mobile = watch('mobile', '');
  const name = watch('name', '');
  
  // Check password strength in real-time
  useEffect(() => {
    if (password) {
      setPasswordStrength({
        score: calculatePasswordStrength(password),
        hasUpper: /[A-Z]/.test(password),
        hasLower: /[a-z]/.test(password),
        hasNumber: /[0-9]/.test(password),
        hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        minLength: password.length >= 8
      });
    } else {
      setPasswordStrength({
        score: 0,
        hasUpper: false,
        hasLower: false,
        hasNumber: false,
        hasSpecial: false,
        minLength: false
      });
    }
  }, [password]);
  
  const calculatePasswordStrength = (pass) => {
    let score = 0;
    if (pass.length >= 8) score += 25;
    if (pass.length >= 12) score += 10;
    if (/[A-Z]/.test(pass)) score += 20;
    if (/[a-z]/.test(pass)) score += 20;
    if (/[0-9]/.test(pass)) score += 20;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pass)) score += 25;
    return Math.min(score, 100);
  };
  
  const getPasswordStrengthColor = () => {
    if (passwordStrength.score < 40) return 'bg-red-500';
    if (passwordStrength.score < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  const getPasswordStrengthText = () => {
    if (passwordStrength.score < 40) return 'Weak';
    if (passwordStrength.score < 70) return 'Medium';
    return 'Strong';
  };
  
  const onSubmit = async (data) => {
    setIsLoading(true);
    setServerErrors({});
    
    try {
      console.log('🚀 Registering user with data:', data);
      const result = await registerUser(data);
      
      console.log('📦 Registration result:', result);
      
      if (result.success) {
        // Store user data for OTP verification
        const pendingData = {
          userId: result.data?.userId || result.userId,
          email: result.data?.email || data.email,
          name: result.data?.name || data.name,
          otp: result.data?.otp || result.otp
        };
        
        localStorage.setItem('pendingVerification', JSON.stringify(pendingData));
        
        toast.success(
          <div className="flex items-center">
            <MdOutlineMarkEmailRead className="text-2xl mr-2" />
            <div>
              <p className="font-semibold">{result.message || 'Registration successful!'}</p>
              <p className="text-sm">Please check your email for OTP verification.</p>
            </div>
          </div>
        );
        
        // Navigate to verify OTP page
        setTimeout(() => {
          navigate('/verify-otp');
        }, 1500);
        
      } else {
        // Handle server-side validation errors
        if (result.errors && Array.isArray(result.errors)) {
          result.errors.forEach(err => {
            if (err.param) {
              setError(err.param, {
                type: 'server',
                message: err.msg
              });
            }
          });
          
          // Show first error as toast
          if (result.errors[0]?.msg) {
            toast.error(result.errors[0].msg);
          }
        } 
        else if (result.errors && typeof result.errors === 'object') {
          Object.entries(result.errors).forEach(([field, message]) => {
            setError(field, {
              type: 'server',
              message: message
            });
          });
          
          // Show first error as toast
          const firstError = Object.values(result.errors)[0];
          if (firstError) {
            toast.error(firstError);
          }
        }
        else if (result.message) {
          toast.error(result.message);
          
          // Check if the error message indicates existing user
          if (result.message.includes('already exists')) {
            if (result.message.includes('email')) {
              setError('email', {
                type: 'server',
                message: 'This email is already registered'
              });
            }
            if (result.message.includes('mobile')) {
              setError('mobile', {
                type: 'server',
                message: 'This mobile number is already registered'
              });
            }
          }
        } else {
          toast.error('Registration failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      
      // Handle network/API errors without showing status code
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.[0]?.msg ||
                          'Registration failed. Please try again.';
      
      toast.error(errorMessage);
      
      // Set field errors if available
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        if (Array.isArray(errors)) {
          errors.forEach(err => {
            if (err.param) {
              setError(err.param, {
                type: 'server',
                message: err.msg
              });
            }
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-gray-50 to-gray-100 m-0 p-0 relative">
      {/* Fixed Background that covers entire viewport and scrolls with content */}
      <div className="fixed inset-0 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 overflow-hidden">
        {/* Animated waves background */}
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute bottom-0 w-full h-48" preserveAspectRatio="none" viewBox="0 0 1440 120">
            <path fill="white" d="M0,32L48,37.3C96,43,192,53,288,58.7C384,64,480,64,576,58.7C672,53,768,43,864,48C960,53,1056,75,1152,80C1248,85,1344,75,1392,69.3L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
        
        {/* Simple Plane Background Pattern - Overlay on gradient */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 transform rotate-12">
            <FaPlane className="text-white text-8xl" />
          </div>
          <div className="absolute bottom-10 right-10 transform -rotate-45">
            <FaPlane className="text-white text-8xl" />
          </div>
          <div className="absolute top-1/3 right-1/4 transform rotate-90">
            <FaPlane className="text-white text-6xl" />
          </div>
          <div className="absolute bottom-1/3 left-1/4 transform -rotate-12">
            <FaPlane className="text-white text-6xl" />
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="relative z-10 min-h-screen w-full overflow-y-auto">
        {/* Main Content Container with padding for scrolling */}
        <div className="flex items-start justify-center min-h-screen pt-16 md:pt-24 pb-8">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              {/* Left side - Welcome text (exactly like login page) */}
              <div className="text-center lg:text-left lg:pt-6">
                <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
                  ✈️ Start Your Journey Today
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight text-white">
                  Join 
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-pink-300">
                    Travel Friend
                  </span>
                </h1>
                <p className="text-lg text-blue-100 max-w-xl mx-auto lg:mx-0 mb-6">
                  Create your account to access exclusive deals, connect with fellow travelers, and start your adventure.
                </p>
                
                {/* Quick Stats - exactly like login page */}
                <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaGlobe className="text-yellow-300" />
                    <span className="text-white">100+ Countries</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaUsers className="text-yellow-300" />
                    <span className="text-white">50K+ Travelers</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaHeart className="text-yellow-300" />
                    <span className="text-white">10K+ Reviews</span>
                  </div>
                </div>
              </div>

              {/* Right side - Registration Form Card */}
              <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-6 transform transition-all duration-500 hover:scale-105">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Create Account</h2>
                    <p className="text-gray-500 text-sm">Join our global community</p>
                  </div>

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {/* Full Name Field */}
                    <div>
                      <InputField
                        label="Full Name"
                        type="text"
                        placeholder="Enter your full name"
                        icon={<FaUser className="text-gray-400" />}
                        error={errors.name || serverErrors.name}
                        {...register('name', {
                          required: 'Name is required',
                          minLength: {
                            value: 2,
                            message: 'Name must be at least 2 characters'
                          },
                          maxLength: {
                            value: 50,
                            message: 'Name cannot exceed 50 characters'
                          },
                          pattern: {
                            value: /^[a-zA-Z\s]*$/,
                            message: 'Name can only contain letters and spaces'
                          }
                        })}
                      />
                      {touchedFields.name && !errors.name && name && (
                        <p className="mt-1 text-xs text-green-600 flex items-center">
                          <FaCheckCircle className="mr-1" /> Valid name format
                        </p>
                      )}
                    </div>
                    
                    {/* Email Field */}
                    <div>
                      <InputField
                        label="Email Address"
                        type="email"
                        placeholder="Enter your email"
                        icon={<FaEnvelope className="text-gray-400" />}
                        error={errors.email || serverErrors.email}
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address'
                          }
                        })}
                      />
                      {touchedFields.email && !errors.email && email && (
                        <p className="mt-1 text-xs text-green-600 flex items-center">
                          <FaCheckCircle className="mr-1" /> Valid email format
                        </p>
                      )}
                    </div>
                    
                    {/* Mobile Number Field */}
                    <div>
                      <InputField
                        label="Mobile Number"
                        type="tel"
                        placeholder="Enter 10-digit mobile number"
                        icon={<FaPhone className="text-gray-400" />}
                        error={errors.mobile || serverErrors.mobile}
                        {...register('mobile', {
                          required: 'Mobile number is required',
                          pattern: {
                            value: /^[0-9]{10}$/,
                            message: 'Please enter a valid 10-digit mobile number'
                          }
                        })}
                      />
                      {touchedFields.mobile && !errors.mobile && mobile && (
                        <p className="mt-1 text-xs text-green-600 flex items-center">
                          <FaCheckCircle className="mr-1" /> Valid mobile number
                        </p>
                      )}
                    </div>
                    
                    {/* Password Field with Strength Meter */}
                    <div>
                      <div className="relative">
                        <InputField
                          label="Password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Create a strong password"
                          icon={<FaLock className="text-gray-400" />}
                          error={errors.password || serverErrors.password}
                          {...register('password', {
                            required: 'Password is required',
                            minLength: {
                              value: 6,
                              message: 'Password must be at least 6 characters'
                            },
                            validate: {
                              hasUpper: (value) => 
                                /[A-Z]/.test(value) || 'Password must contain at least one uppercase letter',
                              hasLower: (value) => 
                                /[a-z]/.test(value) || 'Password must contain at least one lowercase letter',
                              hasNumber: (value) => 
                                /[0-9]/.test(value) || 'Password must contain at least one number',
                              hasSpecial: (value) => 
                                /[!@#$%^&*(),.?":{}|<>]/.test(value) || 'Password must contain at least one special character'
                            }
                          })}
                          className="pr-12"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-12 text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                        </button>
                      </div>
                      
                      {/* Password Strength Meter */}
                      {password && (
                        <div className="mt-2 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                style={{ width: `${passwordStrength.score}%` }}
                                className={`h-full ${getPasswordStrengthColor()} transition-all duration-300`}
                              />
                            </div>
                            <span className="ml-3 text-xs font-medium text-gray-600">
                              {getPasswordStrengthText()}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="flex items-center text-gray-600">
                              {passwordStrength.minLength ? 
                                <FaCheckCircle className="text-green-500 mr-1" /> : 
                                <FaTimesCircle className="text-gray-300 mr-1" />
                              } 8+ characters
                            </div>
                            <div className="flex items-center text-gray-600">
                              {passwordStrength.hasUpper ? 
                                <FaCheckCircle className="text-green-500 mr-1" /> : 
                                <FaTimesCircle className="text-gray-300 mr-1" />
                              } Uppercase
                            </div>
                            <div className="flex items-center text-gray-600">
                              {passwordStrength.hasLower ? 
                                <FaCheckCircle className="text-green-500 mr-1" /> : 
                                <FaTimesCircle className="text-gray-300 mr-1" />
                              } Lowercase
                            </div>
                            <div className="flex items-center text-gray-600">
                              {passwordStrength.hasNumber ? 
                                <FaCheckCircle className="text-green-500 mr-1" /> : 
                                <FaTimesCircle className="text-gray-300 mr-1" />
                              } Number
                            </div>
                            <div className="flex items-center text-gray-600 col-span-2">
                              {passwordStrength.hasSpecial ? 
                                <FaCheckCircle className="text-green-500 mr-1" /> : 
                                <FaTimesCircle className="text-gray-300 mr-1" />
                              } Special character (!@#$%^&*)
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Confirm Password Field */}
                    <div>
                      <div className="relative">
                        <InputField
                          label="Confirm Password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm your password"
                          icon={<FaLock className="text-gray-400" />}
                          error={errors.confirmPassword}
                          {...register('confirmPassword', {
                            required: 'Please confirm your password',
                            validate: value => value === password || 'Passwords do not match'
                          })}
                          className="pr-12"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-12 text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                        </button>
                      </div>
                    </div>
                    
                    {/* Submit Button */}
                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-2 rounded-lg transition-all transform hover:scale-[1.02] disabled:opacity-50"
                      isLoading={isLoading}
                    >
                      Create Account
                    </Button>
                  </form>
                  
                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-gray-500">Already a member?</span>
                    </div>
                  </div>
                  
                  {/* Login Link */}
                  <Link
                    to="/login"
                    className="block text-center text-blue-600 hover:text-blue-700 font-medium transition-colors duration-300"
                  >
                    Sign in to your account
                  </Link>

                  {/* Trust Badges - exactly like login page */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex justify-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FaCheckCircle className="text-green-500" /> Secure Registration
                      </span>
                      <span className="flex items-center gap-1">
                        <FaUsers className="text-blue-500" /> 50K+ Members
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default RegisterPage;